import React, { useState, useRef, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import "./OTPScreen.scss";
import headerLogo from "../../assets/Icons/header.png";
import axiosClient from "../../api/axiosClient";
import apiPaths from "../../api/endpoints";
import apiClientTwo from "../../api/apiClient2";
import { toast } from "react-toastify";
import { identifyUser, setUserProperties, trackEvent } from "../../mixpanel";

interface OTPScreenProps {
  setIsAuthenticated: (value: boolean) => void;
  setNeedsRoleSelection: (value: boolean) => void;
  email: string;
}

function OTPScreen({
  setIsAuthenticated,
  setNeedsRoleSelection,
  email,
}: OTPScreenProps) {
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current is empty
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    pastedData.split("").forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);

    // Focus on the next empty input or the last one
    const nextEmptyIndex = newOtp.findIndex((digit) => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otp.join("");

    if (otpCode.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    trackEvent("OTP_VERIFICATION_ATTEMPTED");
    setError("");
    setIsLoading(true);

    try {
      const response = await axiosClient.get(
        `magic-login?token=${otpCode}&email=${encodeURIComponent(email)}`,
      );
      if (response.data) {
        localStorage.setItem("userAccessToken", response?.data?.accessToken);
        const userData = await apiClientTwo.get(apiPaths.GET_USER);
        if (userData?.data) {
          identifyUser(userData.data?.userName);
          const profileType = userData?.data?.profileType?.toLowerCase();
          if (profileType === "principal" || profileType === "teacher") {
            trackEvent("SUCCESSFUL_LOGIN", { profileType: profileType });
            setUserProperties('school_id', userData.data?.schoolHash)
            setNeedsRoleSelection(true);
            navigate("/role-select");
            // setIsAuthenticated(true);
          } else {
            trackEvent("OTP_VERIFICATION_FAILED_INSUFFICIENT_PERMISSIONS");
            setError(
              "Your account does not have the required permissions. Please contact your school administrator for access.",
            );
          }
        } else {
          setError("Failed to fetch user information");
        }
      }
    } catch (err: any) {
      trackEvent("OTP_VERIFICATION_FAILED", {
        error: err?.response?.data?.message || err.message || "Unknown error",
      });
      console.error("Error verifying OTP:", err);
      setError(
        err.response?.data?.message || "Invalid code. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setOtp(["", "", "", "", "", ""]);
    setError("");
    trackEvent("OTP_RESEND_ATTEMPTED");
    try {
      const response = await axiosClient.post("magic-login", {
        email: email,
      });
      if (response.data) {
        toast.success("OTP code resent successfully!");
      } else {
        toast.error("Failed to resend OTP code. Please try again.");
      }
    } catch (err: any) {
      trackEvent("OTP_RESEND_FAILED", {
        error: err?.response?.data?.error || err.message || "Unknown error",
      });
      console.error("Error resending OTP code:", err);
      toast.error(
        err.response?.data?.error ||
          "Failed to resend OTP code. Please try again.",
      );
    }
  };

  return (
    <div className="otp-container">
      <div className="otp-content">
        <div className="logo-container">
          <img
            src={headerLogo}
            alt="Teacher Dashboard Logo"
            className="logo-image"
          />
        </div>

        <h1 className="main-title">Champion Teacher Workspace</h1>

        <div className="otp-form-wrapper">
          <div className="otp-header">
            <h2 className="otp-heading">Please enter 6-digit code</h2>
            <p className="otp-description">
              We've sent you a one-time code to {email}.
            </p>
          </div>

          <form onSubmit={handleVerifyOTP} className="otp-form">
            <div className="otp-inputs">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="otp-input"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="button-wrapper">
              <button
                type="submit"
                className="continue-button"
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Continue"}
              </button>

              <button
                type="button"
                className="resend-button"
                onClick={handleResendCode}
              >
                Resend code
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default OTPScreen;
